import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { ArrowLeft, BookOpen, ChevronRight, PlayCircle, Eye } from 'lucide-react'
import EditableText from '@/components/edit/EditableText'

export default function PreviewUnitDetail() {
  const { unitId } = useParams<{ unitId: string }>()
  const [unit, setUnit] = useState<any>(null)
  const [lessons, setLessons] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (unitId) loadData()
  }, [unitId])

  const loadData = async () => {
    const [{ data: unitData }, { data: lessonsData }] = await Promise.all([
      supabase.from('units').select('id, order_num, title, description').eq('id', unitId!).single(),
      supabase
        .from('lessons')
        .select('id, order_num, lesson_number, title, description')
        .eq('unit_id', unitId!)
        .eq('is_active', true)
        .order('order_num', { ascending: true })
    ])
    if (unitData) setUnit(unitData)
    if (lessonsData) setLessons(lessonsData)
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (!unit) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Unidad no encontrada</p>
        <Link to="/teacher/preview/units" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
          Volver a unidades
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/teacher/preview/units" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-600 font-medium">Unidad {unit.order_num}</span>
            <span className="text-gray-300">·</span>
            <div className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs text-amber-600 font-medium">Previsualización</span>
            </div>
          </div>
          <EditableText
            as="h1"
            table="units"
            id={unit.id}
            field="title"
            value={unit.title}
            placeholder="(sin título)"
            className="text-2xl font-bold text-gray-900"
            onSaved={(v) => setUnit({ ...unit, title: v })}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 shadow-sm">
        <EditableText
          as="p"
          table="units"
          id={unit.id}
          field="description"
          value={unit.description}
          placeholder="(sin descripción)"
          multiline
          className="text-gray-600"
          onSaved={(v) => setUnit({ ...unit, description: v })}
        />
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Lecciones
        </h2>

        <div className="space-y-3">
          {lessons.map((lesson, index) => (
            <Link
              key={lesson.id}
              to={`/teacher/preview/lesson/${lesson.id}`}
              className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-md transition-all"
            >
              <div className="p-2 rounded-lg bg-blue-100">
                <PlayCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <span className="text-xs text-gray-500">Lección {lesson.order_num || lesson.lesson_number || index + 1}</span>
                <h3 className="font-medium text-gray-900">{lesson.title}</h3>
                {lesson.description && (
                  <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{lesson.description}</p>
                )}
              </div>
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg shrink-0">
                Ver
                <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          ))}

          {lessons.length === 0 && (
            <div className="text-center py-8 bg-white rounded-xl border">
              <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No hay lecciones disponibles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
